import { Trans, useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import type {
  LotoOtherPage,
  LotoPointKey,
} from 'features/lotoReports/lotoIsolationPoints'
import { lotoPageReports } from 'features/lotoReports/lotoPageAddition'
import type { VisitLotoPage } from 'features/lotoReports/useLotoDocumentTab'
import type { LotoPage } from 'features/lotoReports/useLotoPage'
import type { LotoPageAddition } from 'features/lotoReports/useLotoPageAddition'
import type { SelectLoto } from 'features/lotoReports/useLotoPanelSession'
import { useLotoReports } from 'features/lotoReports/useLotoReports'
import type { LotoSession } from 'features/lotoReports/useLotoSession'
import { type ReactElement, useCallback } from 'react'

import { LotoNewDraftForm } from './LotoNewDraftForm'
import { LotoPanelBody, LotoPanelWrap } from './lotoPagePanel.styles'
import { LotoPagePanelHeader } from './LotoPagePanelHeader'
import { LotoReportSelect, NEW_LOTO_DRAFT_VALUE } from './LotoReportSelect'
import { LotoSelectedReport } from './LotoSelectedReport'

type LotoPagePanelProps = {
  page: LotoPage
  session: LotoSession | undefined
  addition: LotoPageAddition
  onClose: () => void
  onSelect: SelectLoto
  onOpenPage: (page: LotoOtherPage) => void
  onVisitPage: VisitLotoPage
  onFocusPoint: (key: LotoPointKey, page?: LotoOtherPage) => void
}

function LotoReportsUnavailable({
  isLoading,
  onRetry,
}: {
  isLoading: boolean
  onRetry: () => void
}): ReactElement {
  if (isLoading)
    return (
      <Text variant='caption' color='muted' role='status'>
        <Trans>Loading LOTO reports…</Trans>
      </Text>
    )
  return (
    <Box display='flex' align='center' justify='between' gap='sm'>
      <Text variant='caption' color='destructive' role='alert'>
        <Trans>Could not load LOTO reports.</Trans>
      </Text>
      <Button variant='outline' size='sm' onClick={onRetry}>
        <Trans>Try again</Trans>
      </Button>
    </Box>
  )
}

export function LotoPagePanel({
  page,
  session,
  addition,
  onClose,
  onSelect,
  onOpenPage,
  onVisitPage,
  onFocusPoint,
}: LotoPagePanelProps): ReactElement {
  const { t } = useLingui()
  const { scope, attachment, documentName, pageNumber } = page
  const selectedId = session?.reportId
  const { reports, error, isLoading, save, refresh } = useLotoReports(scope)
  const { onPage, others } = lotoPageReports(reports, attachment)
  const { isOpen, creating, failedName, open, createDraft } = addition
  const isNewDraft = isOpen && selectedId === undefined
  const reportsReady = !isLoading && error === undefined
  const choose = useCallback(
    (value: string) =>
      value === NEW_LOTO_DRAFT_VALUE
        ? onSelect(undefined, open)
        : onSelect(value),
    [onSelect, open]
  )
  const create = useCallback(
    async (name: string): Promise<boolean> => {
      const reportId = await createDraft({ name, scope, attachment, save })
      if (reportId) onSelect(reportId)
      return reportId !== undefined
    },
    [attachment, createDraft, onSelect, save, scope]
  )
  const retry = useCallback(() => void refresh(), [refresh])
  return (
    <LotoPanelWrap aria-label={t`LOTO panel`}>
      <LotoPanelBody>
        <LotoPagePanelHeader
          documentName={documentName}
          pageNumber={pageNumber}
          reportId={session?.report?.id}
          onClose={onClose}
        />
        <LotoReportSelect
          value={selectedId ?? (isNewDraft ? NEW_LOTO_DRAFT_VALUE : '')}
          onPage={onPage}
          others={others}
          disabled={!reportsReady}
          onChange={choose}
        />
        {!reportsReady && (
          <LotoReportsUnavailable isLoading={isLoading} onRetry={retry} />
        )}
        {isNewDraft && (
          <LotoNewDraftForm
            disabled={creating}
            creating={creating}
            failedName={failedName}
            onCreate={create}
          />
        )}
        {session?.report && (
          <LotoSelectedReport
            page={page}
            session={session}
            report={session.report}
            onOpenPage={onOpenPage}
            onVisitPage={onVisitPage}
            onFocusPoint={onFocusPoint}
          />
        )}
        {reportsReady && session && !session.report && (
          <Text variant='caption' color='muted'>
            <Trans>This LOTO report is no longer available.</Trans>
          </Text>
        )}
      </LotoPanelBody>
    </LotoPanelWrap>
  )
}
